// =============================================================================
// Shared onboarding chrome
//
// Visual shell + sections shared between the referral onboarding page
// (ClientOnboardingPage) and the partner-initiated approval page
// (ClientApprovalPage): the banner/logo card shell, the advisor info header,
// and the post-signup success view.
// =============================================================================

import React from "react";
import {
    APColumn,
    APRow,
    APText,
    APImage,
    APButton,
    APIcon,
    APPalette,
} from "ap-components";
import APAlertBar from "ap-components/src/elements/APAlertBar";
import { AdvisorInfo } from "../WebStory/Models/WebStoryModel";

const DESKTOP_CARD_WIDTH = "592px";
const BANNER_GRADIENT = "linear-gradient(102.8deg, #342970 18.27%, #2b62c0 100%)";

// =============================================================================
// Banner + Logo + Card Shell
// =============================================================================

export function OnboardingCard({
    advisorData,
    hasLogo,
    isMobile,
    children,
    paddingBottom,
}: {
    advisorData: AdvisorInfo | null;
    hasLogo: boolean;
    isMobile: boolean;
    children: React.ReactNode;
    paddingBottom?: string;
}) {
    const logoSize = 100;
    const bannerMarginBottom = hasLogo ? "-40px" : "0px";
    const cardPaddingBottom = paddingBottom ?? "20px";

    return (
        <APColumn
            crossAxisAlignment="center"
            mainAxisAlignment="start"
            mainAxisSize="max"
            style={{
                width: "100%",
                padding: isMobile ? "20px 12px 32px" : 0,
                overflowY: "auto",
            }}
        >
            <div
                style={{
                    width: isMobile ? "100%" : DESKTOP_CARD_WIDTH,
                    backgroundColor: APPalette.white,
                    borderRadius: "16px",
                    overflow: "visible",
                    paddingTop: "16px",
                    paddingLeft: "16px",
                    paddingRight: "16px",
                    paddingBottom: cardPaddingBottom,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    margin: "auto 0",
                }}
            >
                {/* Banner gradient */}
                <div
                    style={{
                        width: "100%",
                        aspectRatio: "1000/250",
                        backgroundImage: advisorData?.image_banner
                            ? `url(${advisorData.image_banner})`
                            : BANNER_GRADIENT,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        borderRadius: "4px",
                        marginBottom: bannerMarginBottom,
                        flexShrink: 0,
                    }}
                />

                {/* Logo circle (overlaps banner via negative margin) */}
                {hasLogo && (
                    <div
                        style={{
                            width: logoSize,
                            height: logoSize,
                            marginBottom: "10px",
                            borderRadius: "246px",
                            backgroundColor: "#c2ddf7",
                            border: `1px solid ${APPalette["grey-100"]}`,
                            overflow: "hidden",
                            flexShrink: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <APImage
                            src={advisorData!.image_logo}
                            height={logoSize}
                            width={logoSize}
                            alt="Advisor Logo"
                            imageFit="cover"
                        />
                    </div>
                )}

                {children}
            </div>
        </APColumn>
    );
}

// =============================================================================
// Advisor Info Section (title, subtitle, MFD/ARN)
// =============================================================================

export function AdvisorInfoSection({
    advisorData,
    isMobile,
}: {
    advisorData: AdvisorInfo;
    isMobile: boolean;
}) {
    return (
        <APColumn crossAxisAlignment="center" gap="8px" style={{ paddingTop: "16px" }}>
            {advisorData.title && (
                <APText
                    variant={isMobile ? "h3" : "h1"}
                    color={APPalette["brand-400"]}
                    center
                >
                    {advisorData.title}
                </APText>
            )}
            {advisorData.subtitle && (
                <APText
                    variant={isMobile ? "paragraph-medium" : "paragraph-large"}
                    color={APPalette["grey-600"]}
                    center
                >
                    {advisorData.subtitle}
                </APText>
            )}
            <APRow gap="20px" mainAxisAlignment="center" crossAxisAlignment="center">
                <APText variant="paragraph-medium" color={APPalette["grey-700"]}>
                    <b>MFD:</b> {advisorData.advisorName}
                </APText>
                <APText variant="paragraph-medium" color={APPalette["grey-700"]}>
                    <b>ARN:</b> {advisorData.arnCode?.replace(/^ARN-/i, "")}
                </APText>
            </APRow>
        </APColumn>
    );
}

// =============================================================================
// Success View
// =============================================================================

export function SuccessView({
    advisorData,
    hasLogo,
    isReferrerOnly,
    downloadLink,
    isMobile,
    clientType,
}: {
    advisorData: AdvisorInfo | null;
    hasLogo: boolean;
    isReferrerOnly: boolean;
    downloadLink: string;
    isMobile: boolean;
    clientType: string;
}) {
    const companyName = isReferrerOnly ? null : advisorData?.advisorName;

    return (
        <OnboardingCard advisorData={advisorData} hasLogo={hasLogo} isMobile={isMobile} paddingBottom="20px">
            <APColumn
                crossAxisAlignment="center"
                gap="24px"
                style={{ width: "100%" }}
            >
                {/* Congratulations text */}
                <APColumn
                    crossAxisAlignment="center"
                    gap="8px"
                    style={{ width: "100%", paddingTop: "16px" }}
                >
                    <APColumn crossAxisAlignment="center">
                        <APText variant={isMobile ? "h3" : "h1"} color={APPalette["brand-400"]} center>
                            Congratulations!
                        </APText>
                        <APText variant={isMobile ? "h3" : "h1"} color={APPalette["brand-400"]} center>
                            You have successfully registered{companyName ? " with" : ""}
                        </APText>
                        {companyName && (
                            <APText variant={isMobile ? "h3" : "h1"} color={APPalette["grey-800"]} center>
                                {companyName}
                            </APText>
                        )}
                    </APColumn>
                    <APText
                        variant={isMobile ? "paragraph-medium" : "paragraph-large"}
                        color={APPalette["grey-700"]}
                        center
                    >
                        You can download the app by clicking the button below.
                    </APText>
                </APColumn>

                {/* Download button */}
                <APButton
                    type="primary"
                    size={isMobile ? "medium" : "large"}
                    leading={<APIcon icon="downloadLine" size={16} color={APPalette.white} />}
                    style={{
                        borderRadius: "8px",
                        backgroundColor: APPalette["brand-300"],
                        width: "100%",
                    }}
                    onClick={() => window.open("https://astp.ls/c/invest", "_blank")}
                >
                    Open / Download App
                </APButton>

                {/* Password note */}
                <APAlertBar
                    severity="info"
                    iconSize={20}
                    content={
                        <APText variant="paragraph-small" color={APPalette["grey-700"]}>
                            <b>Note:</b> {clientType === "minor" ? "The Guardian PAN" : "Your PAN"} will be your default password. Enter it in capital letters. You can change this anytime from your Settings.
                        </APText>
                    }
                    style={{ width: "100%" }}
                />
            </APColumn>
        </OnboardingCard>
    );
}
